import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { useCallback } from "react";
import { Home, BookOpen, Shield, MessageCircle, User, Settings, LogOut, ChevronRight } from "lucide-react";

const MobileNavigation = ({
  setOpen,
  handleSignOut,
}: {
  setOpen: (v: boolean) => void;
  handleSignOut: () => void;
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleApologeticsClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      navigate("/apologetics", { replace: true });
      setOpen(false);
    },
    [navigate, setOpen]
  );

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const menuItems = [
    { 
      path: "/about", 
      label: "About",
      icon: <Home className="h-5 w-5" />
    },
    { 
      path: "/find-your-verse", 
      label: "Find Your Verse",
      icon: <BookOpen className="h-5 w-5" />
    },
    { 
      path: "/bible-tools", 
      label: "Scripture Tools", // Changed from "Bible Tools"
      icon: <BookOpen className="h-5 w-5" />
    },
    { 
      path: "/apologetics", 
      label: "Apologetics",
      icon: <Shield className="h-5 w-5" />,
      onClick: handleApologeticsClick
    },
    { 
      path: "/prayer-generator", 
      label: "Prayer",
      icon: <MessageCircle className="h-5 w-5" />
    },
    { 
      path: "/ministry", 
      label: "Ministry",
      icon: <MessageCircle className="h-5 w-5" />
    }
  ];

  const accountItems = user ? [
    { 
      path: "/dashboard", 
      label: "Dashboard",
      icon: <Home className="h-5 w-5" />
    },
    { 
      path: "/profile", 
      label: "Profile",
      icon: <User className="h-5 w-5" />
    }
  ] : [];

  return (
    <div className="flex flex-col space-y-4">
      {/* Main Navigation Links */}
      <div className="space-y-2">
        {menuItems.map((item) => (
          <div key={item.path} className="animate-fade-in">
            {item.onClick ? (
              <a
                href={item.path}
                className={`flex items-center justify-between px-4 py-3 rounded-lg ${
                  isActivePath(item.path)
                    ? "bg-heaven-gold/10 text-heaven-gold"
                    : "text-gray-700 hover:bg-heaven-gold/5"
                } transition-all duration-200`}
                onClick={item.onClick}
              >
                <div className="flex items-center">
                  <span className="mr-3 text-heaven-gold">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </a>
            ) : (
              <Link
                to={item.path}
                className={`flex items-center justify-between px-4 py-3 rounded-lg ${
                  isActivePath(item.path)
                    ? "bg-heaven-gold/10 text-heaven-gold"
                    : "text-gray-700 hover:bg-heaven-gold/5"
                } transition-all duration-200`}
                onClick={() => setOpen(false)}
              >
                <div className="flex items-center">
                  <span className="mr-3 text-heaven-gold">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Account Section */}
      {user && (
        <>
          <div className="border-t border-heaven-gold/20 my-4 pt-4"></div>
          
          <div className="bg-gray-50/80 rounded-lg p-3">
            <div className="text-gray-500 font-medium px-4 mb-2 text-sm">Account</div>
            
            {accountItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center justify-between px-4 py-3 rounded-lg ${
                  isActivePath(item.path)
                    ? "bg-heaven-gold/10 text-heaven-gold"
                    : "text-gray-700 hover:bg-heaven-gold/5"
                } transition-all duration-200`}
                onClick={() => setOpen(false)}
              >
                <div className="flex items-center">
                  <span className="mr-3 text-heaven-gold">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </Link>
            ))}
            
            <button
              className="flex items-center justify-between w-full px-4 py-3 rounded-lg text-gray-700 hover:bg-heaven-gold/5 transition-all duration-200"
              onClick={() => {
                handleSignOut();
                setOpen(false);
              }}
            >
              <div className="flex items-center">
                <LogOut className="h-5 w-5 mr-3 text-heaven-gold" />
                <span className="font-medium">Sign Out</span>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        </>
      )}

      {/* Sign In Link for logged out users */}
      {!user && (
        <div className="border-t border-heaven-gold/20 my-4 pt-4">
          <Link
            to="/sign-in"
            className="flex items-center justify-between px-4 py-3 mb-2 rounded-lg bg-heaven-gold/10 text-heaven-gold transition-all duration-200"
            onClick={() => setOpen(false)}
          >
            <div className="flex items-center">
              <User className="h-5 w-5 mr-3" />
              <span className="font-medium">Sign In</span>
            </div>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default MobileNavigation;
